/* Companion Print Queue Poller
   ================================
   Runs as a singleton background task in the React app. Every
   `POLL_INTERVAL_MS` it pulls pending print jobs from the backend and
   forwards them to the local Companion Print Agent over loopback.

   Why per-tab / per-app?
   The Companion Agent listens on http://127.0.0.1:9100 — so only the
   browser running on the POS PC where the agent lives can reach it.
   We DON'T want every kiosk / mobile / remote device to pull jobs,
   because they'd race. The toggle lives in localStorage so each device
   independently opts-in («Αυτή η συσκευή τυπώνει στους τοπικούς
   εκτυπωτές»).

   Failure modes:
   * Companion offline → POST throws → /mark-failed bumps retry_count
     and the job stays pending; the next poll retries.
   * Mid-print error from the spooler → companion returns {ok:false},
     we treat same as failure.
   * Backend offline → poll throws, we just skip this tick.
*/
import { http } from "@/lib/api";

const COMPANION_URL = "http://127.0.0.1:9100";
const POLL_INTERVAL_MS = 3000;
const STORAGE_KEY = "companion_print_enabled";

let pollTimer = null;
let currentlyProcessing = false; // simple mutex to avoid overlapping polls
let statusListeners = new Set();
let lastStatus = {
  enabled: false,
  companionOk: null,    // boolean | null  (null = not yet probed)
  pendingCount: 0,
  lastError: null,
  lastSuccessAt: null,
};

const notify = () => {
  for (const cb of statusListeners) {
    try { cb(lastStatus); } catch { /* noop */ }
  }
};

export const subscribeCompanion = (cb) => {
  statusListeners.add(cb);
  cb(lastStatus);
  return () => statusListeners.delete(cb);
};

export const isCompanionEnabled = () => {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; }
  catch { return false; }
};

export const setCompanionEnabled = (on) => {
  try { localStorage.setItem(STORAGE_KEY, on ? "1" : "0"); }
  catch { /* private mode */ }
  lastStatus = { ...lastStatus, enabled: !!on };
  notify();
  if (on) startCompanionPoller();
  else stopCompanionPoller();
};

const pushToCompanion = async (windowsPrinter, text, rawHex) => {
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), 4000);
  try {
    const id = (windowsPrinter || "").trim();
    // Two paths into the v3 print engine:
    //
    //   1. `/raw` — when the backend pre-baked an ESC/POS byte stream
    //      (proformas: text + inline CODE128 barcode + single cut). This
    //      bypasses v3's formatter, giving us pixel-parity with the
    //      desktop browser-print proforma.
    //   2. `/print-ticket` — plain text → v3's kitchen/bar/restaurant
    //      formatter applies bold + cut. Used for everything else.
    //
    // Both paths use the same dual-keyed payload (route/profile/printer)
    // so v2 simple agents that only know `printer` still work in their
    // own text-only mode (they ignore route/profile/raw).
    const endpoint = rawHex ? "/raw" : "/print-ticket";
    const body = rawHex
      ? { route: id, profile: id, printer: id, raw: rawHex }
      : { route: id, profile: id, printer: id, text };
    const res = await fetch(`${COMPANION_URL}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    clearTimeout(tid);
    let resp = {};
    try { resp = await res.json(); } catch { /* not JSON */ }
    if (!res.ok || resp.ok === false) {
      const detail = resp.error
        ? `${resp.error}${resp.printer || resp.route ? ` (id: ${resp.printer || resp.route})` : ""}`
        : `HTTP ${res.status}`;
      return { ok: false, error: detail };
    }
    return { ok: true };
  } catch (e) {
    clearTimeout(tid);
    return { ok: false, error: e.message || "fetch failed" };
  }
};

/** Probe the local Companion to discover available print targets.
 *
 *  Returns: { printers: string[], default: string, routes: string[],
 *             version: string|null } or { error: string }
 *
 *  - `printers` = Windows OS printers (works on both v2 + v3 agents)
 *  - `routes`   = configured route ids in the v3 FText Print Engine
 *                 (kitchen/bar/cashier/tameio…) — empty for v2 agents
 *
 *  This dual probe lets Settings show the correct picklist regardless of
 *  which agent generation the operator has installed.
 */
export const discoverWindowsPrinters = async () => {
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), 3500);
  try {
    const [pRes, hRes] = await Promise.all([
      fetch(`${COMPANION_URL}/printers`, { signal: ctrl.signal }),
      fetch(`${COMPANION_URL}/health`, { signal: ctrl.signal }).catch(() => null),
    ]);
    clearTimeout(tid);
    const pBody = await pRes.json();
    if (!pRes.ok) return { error: pBody.error || `HTTP ${pRes.status}` };
    let routes = [];
    let version = null;
    if (hRes && hRes.ok) {
      try {
        const hBody = await hRes.json();
        version = hBody.version || null;
        if (hBody.routes && typeof hBody.routes === "object") {
          routes = Object.keys(hBody.routes);
        } else if (hBody.printer_profiles && typeof hBody.printer_profiles === "object") {
          routes = Object.keys(hBody.printer_profiles);
        }
      } catch { /* health body not JSON */ }
    }
    return { ...pBody, routes, version };
  } catch (e) {
    clearTimeout(tid);
    return { error: e.message || "Companion δεν απαντά (port 9100)" };
  }
};

const tick = async () => {
  if (!isCompanionEnabled()) { stopCompanionPoller(); return; }
  if (currentlyProcessing) return;
  currentlyProcessing = true;
  try {
    // Atomic claim — flips matching rows from `pending` → `claimed` in a
    // single Mongo op so when several POS-PC tabs poll concurrently each
    // job is forwarded by exactly one of them. Falls back to the legacy
    // list endpoint when the backend is too old to expose `/claim`.
    let jobs = [];
    try {
      const r = await http.post("/print-jobs/claim?limit=20");
      jobs = Array.isArray(r.data) ? r.data : [];
    } catch (claimErr) {
      if (claimErr.response?.status === 404 || claimErr.response?.status === 405) {
        const r2 = await http.get("/print-jobs?pending=true&limit=20");
        jobs = Array.isArray(r2.data) ? r2.data : [];
      } else {
        throw claimErr;
      }
    }
    lastStatus = { ...lastStatus, pendingCount: jobs.length };
    notify();
    // Per-printer failure isolation (Feb 2026 improvement):
    // When a single printer is offline (network down, powered off,
    // paper out) we still want jobs bound for OTHER printers to
    // print. Previously the tick broke on the first failure and
    // held up the whole queue behind one dead printer.
    // We now skip subsequent jobs bound for the SAME printer within
    // this tick (avoids hammering the companion with retries the
    // operator can't do anything about right now) but process every
    // job that targets a healthy destination. Failed jobs remain
    // ``pending`` on the backend (mark-failed resets status), so the
    // NEXT tick retries them automatically.
    const failedPrinters = new Set();
    for (const job of jobs) {
      const printerKey = (job.windows_printer_name || "").trim();
      if (printerKey && failedPrinters.has(printerKey)) {
        // Same-tick short-circuit: this printer already failed once,
        // leave the row pending for the next tick to retry.
        continue;
      }
      const result = await pushToCompanion(job.windows_printer_name, job.body_text, job.raw_hex);
      if (result.ok) {
        try {
          await http.post(`/print-jobs/${job.id}/mark-printed`);
          lastStatus = {
            ...lastStatus,
            companionOk: true,
            lastError: null,
            lastSuccessAt: new Date().toISOString(),
          };
          notify();
        } catch { /* network blip — next tick re-marks */ }
      } else {
        try {
          await http.post(`/print-jobs/${job.id}/mark-failed`, { error: result.error });
        } catch { /* noop */ }
        if (printerKey) failedPrinters.add(printerKey);
        lastStatus = { ...lastStatus, companionOk: false, lastError: result.error };
        notify();
        // DO NOT break — try other printers this tick.
      }
    }
  } catch (e) {
    lastStatus = { ...lastStatus, lastError: e.message || "poll failed" };
    notify();
  } finally {
    currentlyProcessing = false;
  }
};

export const startCompanionPoller = () => {
  if (pollTimer || !isCompanionEnabled()) return;
  lastStatus = { ...lastStatus, enabled: true };
  notify();
  tick(); // immediate first run
  pollTimer = setInterval(tick, POLL_INTERVAL_MS);
};

export const stopCompanionPoller = () => {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  lastStatus = { ...lastStatus, enabled: false };
  notify();
};

// Auto-start on module import if previously enabled — survives reloads.
// Additionally, if the user has NEVER touched the toggle (localStorage key
// missing) and a v3 print engine answers /health on localhost:9100, we
// auto-enable so mobile waiters can print without the owner remembering
// to flip the switch on the POS PC. Explicit OFF (localStorage="0") is
// always respected.
const autoDetectAndEnable = async () => {
  try {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch(`${COMPANION_URL}/health`, { signal: ctrl.signal });
    clearTimeout(tid);
    if (!res.ok) return;
    const body = await res.json();
    if (body && body.ok && body.name && /FText Print Engine/i.test(body.name)) {
      // v3 detected → flip toggle ON (persists), start poller.
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
      lastStatus = { ...lastStatus, enabled: true, companionOk: true };
      notify();
      startCompanionPoller();
    }
  } catch {
    /* v3 not reachable from this device (mobile, remote tab) — no-op */
  }
};

if (typeof window !== "undefined") {
  let raw = null;
  try { raw = localStorage.getItem(STORAGE_KEY); } catch { /* noop */ }
  if (raw === "1") {
    // Previously enabled — restart poller after bootstrap.
    setTimeout(() => startCompanionPoller(), 1500);
  } else if (raw === null) {
    // Never decided — probe for v3 and auto-enable if found. Mobile
    // browsers and remote tabs will silently fail the probe, so they
    // stay off (correct: they have no local printer to reach).
    setTimeout(() => { autoDetectAndEnable(); }, 1500);
  }
  // raw === "0" → user explicitly disabled, leave alone.
}
